import React, { useState, useEffect } from "react";
import "./Data.css";
import "./TrafficInfo.css";

function TrafficInfo() {
  const [trafficData, setTrafficData] = useState({
    totalTripsToday: 9000,
    totalTripsYesterday: 8036,
    peakHour: "5 PM",
    peakHourTrips: 450,
    avgWaitTime: 28,
    busiestFloor: "Ground",
    busiestFloorTrips: 2845,
    floorData: [
      { floor: "Floor 1", stops: 2300 },
      { floor: "Floor 2", stops: 1500 },
      { floor: "Floor 3", stops: 1900 },
      { floor: "Floor 4", stops: 1800 },
      { floor: "Floor 5", stops: 1200 },
      { floor: "Floor 6", stops: 900 },
    ],
    topPerformer: {
      floor: 4,
      elevatorId: "#1",
      totalStops: 1790,
    },
    densityData: [
      { floor: "Ground", in: 450, out: 380 },
      { floor: "Floor 2", in: 320, out: 290 },
      { floor: "Floor 4", in: 410, out: 425 },
      { floor: "Floor 6", in: 280, out: 310 },
    ],
  });

  const tripChangePercent = Math.round(
    ((trafficData.totalTripsToday - trafficData.totalTripsYesterday) /
      trafficData.totalTripsYesterday) *
      100
  );

  const getMaxStops = () => {
    return Math.max(...trafficData.floorData.map((d) => d.stops));
  };

  const maxStops = getMaxStops();

  return (
    <div className="traffic-info-container">
      {/* Header */}
      <div className="traffic-header">
        <h1>Traffic & Building Density</h1>
        <div className="view-toggle">
          <button className="toggle-btn active">Simplified</button>
          <button className="toggle-btn">Detailed</button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="metrics-grid">
        <div className="metric-card total-trips">
          <div className="metric-header">
            <span className="metric-label">Total Trips Today</span>
            <span className="metric-icon">📈</span>
          </div>
          <div className="metric-value">{trafficData.totalTripsToday.toLocaleString()}</div>
          <div className={`metric-change ${tripChangePercent >= 0 ? "positive" : "negative"}`}>
            {tripChangePercent >= 0 ? "+" : ""}{tripChangePercent}% vs yesterday
          </div>
        </div>

        <div className="metric-card peak-hour">
          <div className="metric-header">
            <span className="metric-label">Peak Hour</span>
            <span className="metric-icon">⏰</span>
          </div>
          <div className="metric-value">{trafficData.peakHour}</div>
          <div className="metric-subtext">{trafficData.peakHourTrips} trips</div>
        </div>

        <div className="metric-card avg-wait">
          <div className="metric-header">
            <span className="metric-label">Avg Wait Time</span>
            <span className="metric-icon">⏱️</span>
          </div>
          <div className="metric-value">{trafficData.avgWaitTime} <span className="unit">sec</span></div>
          <div className="metric-subtext">Within target range</div>
        </div>

        <div className="metric-card busiest-floor">
          <div className="metric-header">
            <span className="metric-label">Busiest Floor</span>
            <span className="metric-icon">📍</span>
          </div>
          <div className="metric-value">{trafficData.busiestFloor}</div>
          <div className="metric-subtext">{trafficData.busiestFloorTrips.toLocaleString()} trips</div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        {/* Average Stops Per Floor Chart */}
        <div className="chart-container avg-stops-chart">
          <div className="chart-header">
            <h2>Average Stops Per Floor</h2>
            <span className="chart-icon">📊</span>
          </div>
          <div className="bar-chart">
            {trafficData.floorData.map((data, index) => (
              <div key={index} className="bar-wrapper">
                <div className="bar-container">
                  <div
                    className="bar"
                    style={{
                      height: `${(data.stops / maxStops) * 100}%`,
                    }}
                  >
                    <span className="bar-value">{data.stops}</span>
                  </div>
                </div>
                <span className="bar-label">{data.floor}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Performer Card */}
        <div className="chart-container top-performer-card">
          <div className="chart-header">
            <h2>Floor {trafficData.topPerformer.floor} Top Performer</h2>
            <span className="chart-icon">🏆</span>
          </div>
          <div className="performer-content">
            <div className="performer-rank">{trafficData.topPerformer.elevatorId}</div>
            <div className="performer-title">Elevator with Most Stops</div>
            <div className="performer-stats">
              <div className="stat-row">
                <span className="stat-label">Total Stops</span>
                <span className="stat-value">{trafficData.topPerformer.totalStops.toLocaleString()}</span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: "92%" }}
                ></div>
              </div>
              <div className="stat-subtext">On Floor {trafficData.topPerformer.floor} Today</div>
            </div>
          </div>
        </div>
      </div>

      {/* Density Analysis */}
      <div className="density-section">
        <div className="density-header">
          <h2>Building Density Analysis</h2>
          <span className="density-icon">👥</span>
        </div>
        <div className="density-table-wrapper">
          <table className="density-table">
            <thead>
              <tr>
                <th>Floor</th>
                <th className="in-col">Going In</th>
                <th className="out-col">Getting Out</th>
                <th className="flow-col">Net Flow</th>
              </tr>
            </thead>
            <tbody>
              {trafficData.densityData.map((data, index) => (
                <tr key={index}>
                  <td className="floor-name">{data.floor}</td>
                  <td className="in-value">
                    <span className="badge in-badge">{data.in}</span>
                  </td>
                  <td className="out-value">
                    <span className="badge out-badge">{data.out}</span>
                  </td>
                  <td className="flow-value">
                    <span className={`flow-badge ${data.in > data.out ? "positive" : "negative"}`}>
                      {data.in - data.out > 0 ? "+" : ""}{data.in - data.out}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default TrafficInfo;
